import {
  TIER_LABEL,
  featureTierLabel,
  hasFeature,
  isPreviewingFeature,
  type FeatureKey,
  type Tier,
} from '@auctisync/shared';

/**
 * The per-row decision behind PlanPanel, kept pure so it can be tested without
 * a component-rendering stack — the same idiom as previewFrame.logic.ts.
 *
 * The subtlety worth isolating: a row can carry a plan name for two opposite
 * reasons. On a trial it captions something you CAN see ("Included with
 * Professional" — the note Jim asked for). On a paid plan it captions something
 * you CANNOT ("Professional and above"). Rendering both from one careless
 * expression would tell a paying Starter customer that a feature they do not
 * have is included, which is worse than saying nothing.
 */
export type PlanRowState = {
  included: boolean;
  /** The cheapest plan carrying this feature, e.g. "Professional". */
  carriedBy: string | null;
  /** Caption for a feature visible only because the tenant is trialling. */
  previewBadge: string | null;
  /** Caption for a feature the tenant does not have. */
  upgradeBadge: string | null;
};

export function planRowState(tier: Tier, feature: FeatureKey): PlanRowState {
  const included = hasFeature(tier, feature);
  const carriedBy = featureTierLabel(feature);
  const preview = isPreviewingFeature(tier, feature);

  return {
    included,
    carriedBy,
    previewBadge: preview && carriedBy ? `Included with ${carriedBy}` : null,
    upgradeBadge: !included && carriedBy ? `${carriedBy} and above` : null,
  };
}

/**
 * Is this a tier the entitlement ladder actually knows? An unrecognised tier is
 * the exact bug `complimentary` and `trial` each had, so the panel says so
 * rather than rendering a table of confident falses.
 */
export function isKnownTier(tier: string | null | undefined): tier is Tier {
  return typeof tier === 'string' && tier in TIER_LABEL;
}
